import { useCallback, useState } from 'react';

import { Box } from '@opendoor/bricks/core';
import { ButtonSpinner } from '@opendoor/bricks/core/Button/Button';
import Field from '@opendoor/cinderblocks/growth/novo/inputs/Field';
import { IAccountFormProps } from '@opendoor/cinderblocks/platform/auth/AccountForm';
import { debounce } from 'lodash';
import isEmail from 'validator/lib/isEmail';

const AccountEmailInput: IAccountFormProps['EmailInput'] = ({
  errorMessage,
  customEmailValidityCheck,
  delay,
  ...props
}) => {
  const [hasError, setHasError] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // disable exhaustive-deps because the lint is confused by the debounce fn
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validateEmail = useCallback(
    debounce((value: string) => {
      if (customEmailValidityCheck) {
        if (value) {
          setIsValidating(true);
          customEmailValidityCheck(value).then(({ valid }) => {
            setIsValidating(false);
            if (valid) {
              setHasError(false);
            } else {
              setHasError(true);
            }
            props.onChange(undefined, value, valid);
          });
        } else {
          props.onChange(undefined, value, false);
        }
      }
      return () => {};
    }, delay),
    [customEmailValidityCheck],
  );

  return (
    <Box textAlign="left">
      <Field
        errorText={errorMessage}
        label="Continue with email"
        input={{
          id: props.id,
          error: hasError,
          suffix: isValidating ? (
            <div style={{ position: 'relative' }}>
              <ButtonSpinner color="warmgrey950" />
            </div>
          ) : undefined,
          type: 'email',
          onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;

            if (customEmailValidityCheck) {
              validateEmail(value);

              props.onChange(event, value, false);
              return;
            }

            // default email checking
            const isValid = isEmail(value);

            // Clear out any errors if we get a valid email
            if (value && isValid) {
              setHasError(false);
            }

            props.onChange(event, value, isValid);
          },
          onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
            const value = event.target.value;

            if (value && !customEmailValidityCheck) {
              if (isEmail(value)) {
                setHasError(false);
              } else {
                setHasError(true);
              }
            }
          },
          defaultValue: props.value ?? '',
          name: props.name,
          placeholder: props.placeholder,
        }}
      />
    </Box>
  );
};

export default AccountEmailInput;
